import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';
import { colorPalette, EVENTS, QUALITY_METRICS_CASE_FILTER, ROUTES } from 'shared/constants';
import { useDateFormatter } from 'shared/hooks/dates.hooks';
import {
  Table,
  RadioSetFilter,
  radioSetFilterDoesFilterPass,
} from 'src/components/design-system/table';
import { SearchBox } from 'src/components/design-system';
import { Loader } from 'src/components/core';
import analyticsService from 'src/helpers/analytics.service';
import { useQualityMetricsContext } from '../../quality-metrics-context';
import { useMetricCases } from './use-metric-cases';
import styles from './cases-tab.module.scss';

function toTitleCase(str) {
  if (!str) return '';
  return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
}

function ComplianceCell({ value, data }) {
  const isCompliant = data?.compliant;
  return (
    <span className={styles.complianceCell}>
      <FontAwesomeIcon
        icon={isCompliant ? faCheckCircle : faTriangleExclamation}
        color={isCompliant ? colorPalette.greenPositive : colorPalette.redNegative}
      />
      <span
        className={styles.complianceText}
        style={{ color: isCompliant ? colorPalette.greenPositive : colorPalette.redNegative }}>
        {value}
      </span>
    </span>
  );
}

export function CasesTab() {
  const { selectedMetricId, qualityMetric, lang } = useQualityMetricsContext();
  const { formatLongDateTime } = useDateFormatter();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');

  const { cases, totalCases, totalCompliant, loading, error } = useMetricCases({
    metricId: selectedMetricId,
  });

  // `filterChanged` also fires for the search box, so dedupe on the compliance value.
  const lastCaseFilterRef = useRef(QUALITY_METRICS_CASE_FILTER.ALL);

  const handleFilterChanged = useCallback(
    event => {
      const complianceValue = event.api.getFilterModel()?.compliant?.value;

      let filter = QUALITY_METRICS_CASE_FILTER.ALL;
      if (complianceValue === lang.COMPLIANT) {
        filter = QUALITY_METRICS_CASE_FILTER.COMPLIANT;
      } else if (complianceValue === lang.NON_COMPLIANT) {
        filter = QUALITY_METRICS_CASE_FILTER.NON_COMPLIANT;
      }

      if (filter === lastCaseFilterRef.current) return;
      lastCaseFilterRef.current = filter;

      analyticsService.track(EVENTS.QUALITY_METRICS.CASE_FILTER_CLICK, {
        filter,
        totalCases,
        totalCompliant,
        metricId: selectedMetricId,
      });
    },
    [lang, selectedMetricId, totalCases, totalCompliant],
  );

  const openCase = useCallback(
    caseData => {
      if (!caseData?.id) return;

      analyticsService.track(EVENTS.QUALITY_METRICS.CASE_OPEN, {
        title: caseData.title,
        caseId: caseData.id,
        metricId: selectedMetricId,
        metricName: qualityMetric?.title,
      });

      navigate(ROUTES.QUALITY_METRIC_CASES.replace(':id', caseData.id));
    },
    [navigate, selectedMetricId, qualityMetric?.title],
  );

  const handleCellClicked = useCallback(params => openCase(params.data), [openCase]);

  const handleCellKeyDown = useCallback(
    params => {
      if (params.event.key === 'Enter') {
        openCase(params.data);
      }
    },
    [openCase],
  );

  const complianceFilterOptions = useMemo(
    () => [
      { value: lang.COMPLIANT, label: lang.COMPLIANT, matcher: v => v === lang.COMPLIANT },
      {
        value: lang.NON_COMPLIANT,
        label: lang.NON_COMPLIANT,
        matcher: v => v === lang.NON_COMPLIANT,
      },
    ],
    [lang],
  );

  const columnDefs = useMemo(
    () => [
      {
        field: 'title',
        headerName: lang.CASE_PROCEDURE,
        flex: 2,
        minWidth: 200,
        valueFormatter: params => toTitleCase(params.value),
        tooltipValueGetter: params => toTitleCase(params.value),
        cellClass: styles.procedureCell,
        sortable: true,
      },
      {
        field: 'date',
        headerName: lang.CASE_DATE,
        flex: 1,
        minWidth: 180,
        valueFormatter: params => (params.value ? formatLongDateTime(params.value) : ''),
        sortable: true,
      },
      {
        field: 'compliant',
        headerName: lang.COMPLIANCE_HEADER,
        flex: 1,
        minWidth: 150,
        cellRenderer: ComplianceCell,
        valueGetter: params => (params.data?.compliant ? lang.COMPLIANT : lang.NON_COMPLIANT),
        filter: {
          component: RadioSetFilter,
          doesFilterPass: radioSetFilterDoesFilterPass,
        },
        filterParams: {
          options: complianceFilterOptions,
        },
      },
    ],
    [lang, formatLongDateTime, complianceFilterOptions],
  );

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <p className={styles.empty}>{lang.NO_DATA}</p>;
  }

  if (!cases.length) {
    return <p className={styles.empty}>{lang.NO_CASES}</p>;
  }

  return (
    <div className={styles.container}>
      <SearchBox
        className={styles.searchBox}
        size="large"
        placeholder={lang.SEARCH_PLACEHOLDER}
        onChange={e => setSearchText(e.target.value)}
        value={searchText}
      />
      <Table
        enableActiveFilters
        rowData={cases}
        columnDefs={columnDefs}
        pagination={true}
        paginationPageSize={20}
        getRowId={params => params.data.id}
        quickFilterText={searchText}
        containerProps={{ className: styles.tableWrapper }}
        onFilterChanged={handleFilterChanged}
        onCellClicked={handleCellClicked}
        onCellKeyDown={handleCellKeyDown}
        rowClass={styles.clickableRow}
      />
    </div>
  );
}
